import { useState } from 'react';
import { RowListSkeleton } from '../../components/RowListSkeleton';
import { useToast } from '../../lib/use-toast';
import { getDriverDocumentDownloadUrl, type DriverDocumentListItem, type DriverDocumentType } from './drivers.api';
import { DriverDocumentRow } from './DriverDocumentRow';
import { DriverDocumentUploadModal } from './DriverDocumentUploadModal';
import { useDriverDocumentTypes } from './use-driver-document-types';
import type { UploadDriverDocumentInput } from './use-driver-detail';

interface DriverDocumentsProps {
  driverId: string;
  documents: DriverDocumentListItem[];
  canManage: boolean;
  onUpload: (input: UploadDriverDocumentInput) => Promise<boolean>;
}

/** Three fixed slots — licence, medical certificate, passport — from the driver document-type catalogue, each attached or replaced, never versioned. */
export function DriverDocuments({ driverId, documents, canManage, onUpload }: DriverDocumentsProps) {
  const documentTypes = useDriverDocumentTypes();
  const [uploadFor, setUploadFor] = useState<DriverDocumentType | null>(null);
  const [uploading, setUploading] = useState(false);
  const toast = useToast();

  async function handleUpload(input: UploadDriverDocumentInput): Promise<boolean> {
    setUploading(true);
    try {
      return await onUpload(input);
    } finally {
      setUploading(false);
    }
  }

  async function download(documentTypeCode: string) {
    try {
      const { url } = await getDriverDocumentDownloadUrl(driverId, documentTypeCode);
      window.open(url, '_blank', 'noopener');
    } catch {
      toast.error("Couldn't open the document");
    }
  }

  return (
    <section className="flex flex-col gap-4">
      <h2 className="text-sm font-semibold text-fg">Documents</h2>
      {!documentTypes && <RowListSkeleton label="Loading documents" />}
      <ul className="flex flex-col gap-3">
        {documentTypes?.map((documentType) => (
          <DriverDocumentRow
            key={documentType.code}
            documentType={documentType}
            attached={documents.find((document) => document.documentTypeCode === documentType.code)}
            canManage={canManage}
            onDownload={download}
            onUpload={setUploadFor}
          />
        ))}
      </ul>
      <DriverDocumentUploadModal
        open={uploadFor !== null}
        documentType={uploadFor}
        uploading={uploading}
        onUpload={handleUpload}
        onClose={() => setUploadFor(null)}
      />
    </section>
  );
}
